import Modal from 'Common/Components/Modal';
import { AnimatedListSkeleton, AnimatedTable, AnimatedTableBody, AnimatedTableRow } from 'components/animations';
import ButtonBase from 'components/ui/buttons/ButtonBase';
import React, { FC, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import { getInventoryHistorybyProductThunk } from '@redux/features/product.slice';
import { dateFormatter } from 'helpers/formatter_helper';

type IProps = {
    show: boolean;
    handleClose: () => void;
    productId: number;
};

const InventoryProductHistoryModal: FC<IProps> = ({ show, handleClose, productId }) => {
    const dispatch = useAppDispatch();
    const { inventoryHistory, inventoryLoading } = useAppSelector((state: RootState) => state.productState);

    useEffect(() => {
        if (show && productId > 0) {
            dispatch(getInventoryHistorybyProductThunk(productId));
        }
    }, [show, productId, dispatch]);

    return (
        <Modal
            show={show}
            onHide={handleClose}
            dialogClassName="w-full bg-white p-3 shadow rounded-md overflow-scroll md:w-[90%] md:max-w-[1200px] md:max-h-[80vh]"
        >
            <Modal.Header>
                <Modal.Title className="text-lg font-semibold">
                    Inventory Product History - Product ID: {productId}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="my-5">
                {inventoryLoading ? (
                    <div className="mx-auto flex my-6 items-center justify-center rounded-md bg-white">
                        <div className="size-10 animate-spin rounded-full border-4 border-custom-500 border-l-transparent" />
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <AnimatedTable className="table-custom w-full border-separate border-spacing-y-1 whitespace-nowrap">
                            <thead className="ltr:text-left rtl:text-right">
                                <tr
                                    className="relative rounded-md bg-slate-50 after:absolute after:bottom-0
                    after:left-0 after:top-0 after:border-l-2
                    after:border-transparent [&.active]:after:border-custom-500"
                                >
                                    <th className="px-3.5 py-2.5 font-semibold uppercase">ID</th>
                                    <th className="px-3.5 py-2.5 font-semibold uppercase">Transaction Date</th>
                                    <th className="px-3.5 py-2.5 font-semibold uppercase">Type</th>
                                    <th className="px-3.5 py-2.5 font-semibold uppercase">Reference</th>
                                    <th className="px-3.5 py-2.5 font-semibold uppercase">External Reference</th>
                                    <th className="px-3.5 py-2.5 font-semibold uppercase">Quantity</th>
                                </tr>
                            </thead>
                            <AnimatedTableBody loading={false} itemCount={inventoryHistory?.length ?? 0} animationKey={`inventory-history`}>
                                {inventoryHistory && inventoryHistory.length > 0 ? (
                                    inventoryHistory.map((transaction: any) => (
                                        <AnimatedTableRow
                                            rowKey={transaction?.id}
                                            detailRecord={transaction}
                                            className="relative rounded-md bg-slate-50 after:absolute after:bottom-0
                        after:left-0 after:top-0 after:border-l-2 hover:bg-[#bd902e10]
                        after:border-transparent [&.active]:after:border-custom-500"
                                        >
                                            <td className="px-3.5 py-2.5">{transaction?.id || `-`}</td>
                                            <td className="px-3.5 py-2.5">
                                                {transaction?.transactionDate
                                                    ? dateFormatter(transaction.transactionDate)
                                                    : `-`}
                                            </td>
                                            <td className="px-3.5 py-2.5">{transaction?.type || `-`}</td>
                                            <td className="px-3.5 py-2.5">{transaction?.reference || `-`}</td>
                                            <td className="px-3.5 py-2.5">
                                                {transaction?.externalReference || transaction?.ExternalReference || `-`}
                                            </td>
                                            <td className="px-3.5 py-2.5">{transaction?.quantity ?? `-`}</td>
                                        </AnimatedTableRow>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={6} className="px-3.5 py-2.5 text-center text-gray-500">
                                            No inventory history found
                                        </td>
                                    </tr>
                                )}
                            </AnimatedTableBody>
                        </AnimatedTable>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className="flex items-center justify-center gap-2 mt-5">
                <ButtonBase
                    innerText="Close"
                    className="bg-gray-500 hover:bg-gray-600 rounded border-none px-4 py-2 text-white"
                    onClick={handleClose}
                />
            </Modal.Footer>
        </Modal>
    );
};

export default InventoryProductHistoryModal;

